import { graphql } from 'gatsby'
import React from 'react'

import { TreatmentsQueryQuery } from '../../types/graphql-types'
import EnhancedMeta from '../components/meta/enhanced-meta'
import Layout from '../components/layout/layout'
import Gallery from '../components/gallery'
import Appointment from '../components/appointment'
import { generateLocalBusinessSchema } from '../utils/schema-generator'

interface Props {
  data: TreatmentsQueryQuery
  location: Location
}

const Treatments: React.FC<Props> = ({ data, location }: Props) => {
  const meta = data.site?.meta
  const localBusinessSchema = generateLocalBusinessSchema()

  return (
    <Layout location={location}>
      <EnhancedMeta
        title="Aesthetic Treatments Cheshunt | Botox, Fillers & Beauty | Ada Aesthetics"
        description="Discover our range of aesthetic treatments in Cheshunt: Botox, dermal fillers, Russian lips, lash lift, brow lamination & more. Expert treatments from £45. Book now!"
        keywords="aesthetic treatments Cheshunt, botox, dermal fillers, lip fillers, lash lift, brow lamination, beauty treatments Hertfordshire"
        url="https://adaaesthetics.uk/treatments"
        schemaData={localBusinessSchema}
        site={meta}
      />

      <section className="ls s-pt-70 s-pb-50 s-py-lg-100 s-pt-xl-150 s-pb-xl-100">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <h1 className="special-heading">Our Aesthetic Treatments</h1>
              <p className="excerpt" style={{ maxWidth: '800px', margin: '0 auto 40px' }}>
                At Ada Aesthetics in Cheshunt, we offer a full range of medical-grade aesthetic and beauty treatments –
                from Botox and dermal fillers to advanced skin and brow services. Every treatment is delivered by
                experienced practitioners using premium, licensed products for natural-looking results you can trust.
              </p>
            </div>
          </div>

          <div className="row" style={{ marginBottom: '60px' }}>
            <div className="col-12 col-md-6">
              <h2 style={{ fontSize: '24px', marginBottom: '20px' }}>Injectable Treatments</h2>
              <ul style={{ lineHeight: '2', fontSize: '16px' }}>
                <li>
                  <a href="/anti-wrinkle-injections-cheshunt" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <strong>Anti-Wrinkle Injections (Botox)</strong> - From £175
                  </a>
                  <br />
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    Smooth forehead lines, frown lines, and crow's feet
                  </span>
                </li>
                <li>
                  <a href="/dermal-fillers-cheshunt" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <strong>Dermal Fillers</strong> - From £115
                  </a>
                  <br />
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    Restore facial volume and enhance features
                  </span>
                </li>
                <li>
                  <a href="/lip-fillers" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <strong>Lip Fillers</strong> - From £115
                  </a>
                  <br />
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    Add volume and definition to your lips
                  </span>
                </li>
                <li>
                  <a href="/russian-lips" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <strong>Russian Lips</strong> - From £115
                  </a>
                  <br />
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    Heart-shaped lips with natural elevation
                  </span>
                </li>
                <li>
                  <a href="/cheek-fillers" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <strong>Cheek Fillers</strong> - From £149.99
                  </a>
                  <br />
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    Create definition and restore mid-face volume
                  </span>
                </li>
                <li>
                  <a href="/tear-trough-filer" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <strong>Tear Trough Fillers</strong> - From £149.99
                  </a>
                  <br />
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    Reduce under-eye dark circles and hollowing
                  </span>
                </li>
                <li>
                  <a href="/fat-dissolving" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <strong>Fat Dissolving Injections</strong> - From £99
                  </a>
                  <br />
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    Non-surgical body contouring with Lemon Bottle & Aqualyx
                  </span>
                </li>
              </ul>
            </div>

            <div className="col-12 col-md-6">
              <h2 style={{ fontSize: '24px', marginBottom: '20px' }}>Beauty Treatments</h2>
              <ul style={{ lineHeight: '2', fontSize: '16px' }}>
                <li>
                  <a href="/dermaplaning" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <strong>Dermaplaning</strong> - From £49.99
                  </a>
                  <br />
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    Remove dead skin and peach fuzz for glowing skin
                  </span>
                </li>
                <li>
                  <a href="/eyebrows-lamination-and-tint" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <strong>Brow Lamination & Tint</strong> - From £45
                  </a>
                  <br />
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    Transform unruly brows into perfectly groomed eyebrows
                  </span>
                </li>
                <li>
                  <a href="/lash-lift-and-tint" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <strong>Lash Lift & Tint</strong> - £45
                  </a>
                  <br />
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    Curl and lift your natural lashes for 6-8 weeks
                  </span>
                </li>
                <li>
                  <a href="/microblading" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <strong>Microblading & Nanoblading</strong>
                  </a>
                  <br />
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    Semi-permanent eyebrow enhancement
                  </span>
                </li>
              </ul>

              <div style={{ marginTop: '40px', padding: '20px', backgroundColor: '#f8f8f8', borderRadius: '8px' }}>
                <h3 style={{ fontSize: '20px', marginBottom: '15px' }}>Why Choose Ada Aesthetics?</h3>
                <ul style={{ fontSize: '14px', lineHeight: '1.8', marginBottom: '0' }}>
                  <li>Experienced Registered Aesthetic Nurse</li>
                  <li>Premium, licensed products</li>
                  <li>Natural-looking results</li>
                  <li>Free consultations</li>
                  <li>Competitive pricing from £45</li>
                  <li>Convenient Cheshunt location</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Gallery />

      <section className="ls text-center s-py-50">
        <div className="container">
          <h3 style={{ marginBottom: '20px' }}>Ready to Book Your Treatment?</h3>
          <p style={{ maxWidth: '600px', margin: '0 auto 30px' }}>
            Discover more treatment options and book your appointment at our Cheshunt clinic.
            Click below to view our full service menu and available appointments.
          </p>
        </div>
      </section>

      <Appointment />
    </Layout>
  )
}

export default Treatments

export const pageQuery = graphql`
  query TreatmentsQuery {
    site {
      meta: siteMetadata {
        title
        description
        siteUrl
        author
      }
    }
  }
`
